import React, { useEffect, useState } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { Message } from "primereact/message";
import { Dropdown } from "primereact/dropdown";
import { RadioButton } from "primereact/radiobutton";
import { InputText } from "primereact/inputtext";

import TextInputField from "../../../../components/inputs/TextInputField";
import { InputLabel } from "../../../../components/inputs/InputLabel";
import { ProspectDetail, ProspectUpdatePayload } from "../../../../types/onboarding";
import { isEditableState } from "../../../../utils/onboardingStates";
import { validateEuVatId } from "../../../../utils/euVatId";
import useLocationTypes from "../../../../hooks/useLocationTypes";
import {
  LEAD_SOURCE_NEEDING_DETAIL,
  LEAD_SOURCE_OPTIONS,
  SALUTATION_OPTIONS,
  SIGN_METHOD_OPTIONS,
} from "../../../../constants/contractSigning";

interface EditProspectDialogProps {
  visible: boolean;
  prospect: ProspectDetail;
  onHide: () => void;
  onSave: (payload: ProspectUpdatePayload) => Promise<boolean>;
}

// Fields ops may edit in the MVP. (Pipeline state, flags and technical names
// are changed through dedicated actions, not this form.)
const EDITABLE_FIELDS: (keyof ProspectUpdatePayload)[] = [
  "first_name",
  "last_name",
  "email",
  "telephone",
  "company_name",
  "vat_number",
  "billing_street",
  "billing_postcode",
  "billing_city",
  "billing_country",
  "station_street",
  "station_postcode",
  "station_city",
  "station_country",
  "wallbox_manufacturer",
  "wallbox_model",
  "account_holder_name",
  "iban",
  "bic",
  "bank_name",
  // Contract-signing intake fields (CWA-78). discovery_source is intentionally
  // NOT editable here — it is superseded by lead_source (spec research R6) but
  // still round-tripped in REQUEST_FIELDS below.
  "salutation",
  "sign_method",
  "lead_source",
  "referred_by",
  // Where the station is used (CWA-91). Read back as a label, written as an
  // identifier — handled specially below, never through the generic paths.
  "location_type",
];

// Charge points per wallbox, as the form holds them: text, so a field can be
// cleared while typing. An older backend sends no breakdown, only the total —
// the form then starts from one wallbox holding it.
const initialWallboxes = (prospect: ProspectDetail): string[] =>
  prospect.station_evse_counts?.length
    ? prospect.station_evse_counts.map(String)
    : [String(Math.max(1, prospect.charge_point_count || 1))];

const isWholeCount = (value: string): boolean => /^[1-9]\d*$/.test(value.trim());

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

// Lightweight client-side format checks; the server is authoritative (422).
const IBAN_RE = /^[A-Z]{2}\d{2}[A-Z0-9]{10,30}$/;
const BIC_RE = /^[A-Z]{6}[A-Z0-9]{2}([A-Z0-9]{3})?$/;

const fieldLabels: Record<string, string> = {
  first_name: "First name",
  last_name: "Last name",
  email: "Email",
  telephone: "Phone",
  company_name: "Company name",
  vat_number: "VAT number",
  billing_street: "Billing street",
  billing_postcode: "Billing postcode",
  billing_city: "Billing city",
  billing_country: "Billing country",
  station_street: "Station street",
  station_postcode: "Station postcode",
  station_city: "Station city",
  station_country: "Station country",
  wallbox_manufacturer: "Manufacturer",
  wallbox_model: "Model",
  account_holder_name: "Account holder",
  iban: "IBAN",
  bic: "BIC",
  bank_name: "Bank name",
  salutation: "Salutation",
  sign_method: "Signature method",
  lead_source: "Lead source",
  referred_by: "How they found us",
  location_type: "Where the station is used",
};

/**
 * US3: ops edit form. Reuses the shared inputs, validates client-side, and maps
 * server 422 field errors to inline messages. Read-only when the prospect is at
 * or past the edit-lock state (FR-016) — not reachable in the MVP.
 */
const EditProspectDialog: React.FC<EditProspectDialogProps> = ({
  visible,
  prospect,
  onHide,
  onSave,
}) => {
  const [form, setForm] = useState<Record<string, any>>({});
  // Customers sometimes get the hardware wrong; ops correct it here (CWA-201).
  const [wallboxes, setWallboxes] = useState<string[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  // Fetched only while the dialog is open — an unrelated edit must not depend on
  // this lookup being reachable.
  const locationTypes = useLocationTypes(visible);

  const editable = isEditableState(prospect.current_state);
  const isBusiness = prospect.customer_type === "BUSINESS";

  useEffect(() => {
    if (!visible) return;
    const initial: Record<string, any> = {};
    EDITABLE_FIELDS.forEach((f) => {
      initial[f] = (prospect as any)[f] ?? "";
    });
    // The prospect carries the location type's label; the form and the payload
    // must hold the identifier. Left null until the lookup resolves it.
    initial.location_type = null;
    setForm(initial);
    setWallboxes(initialWallboxes(prospect));
    setErrors({});
  }, [visible, prospect]);

  // Resolve the displayed label back to the identifier once the lookup lands,
  // so the dropdown shows the current value instead of looking empty.
  useEffect(() => {
    if (!visible || !locationTypes.options.length) return;
    setForm((prev) => ({
      ...prev,
      location_type: locationTypes.valueForLabel(prospect.location_type),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, locationTypes.options, prospect.location_type]);

  const setField = (name: string, value: any) => {
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next[name];
      return next;
    });
  };

  const clearWallboxErrors = () =>
    setErrors((prev) => {
      const next = { ...prev };
      delete next.station_evse_counts;
      return next;
    });

  const setChargePoints = (index: number, value: string) => {
    setWallboxes((prev) => prev.map((v, i) => (i === index ? value : v)));
    clearWallboxErrors();
  };

  const addWallbox = () => {
    setWallboxes((prev) => [...prev, "1"]);
    clearWallboxErrors();
  };

  const removeWallbox = (index: number) =>
    setWallboxes((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== index) : prev));

  const wallboxCounts = (): number[] => wallboxes.map((v) => parseInt(v.trim(), 10));

  // Untouched, a breakdown guessed for an older backend must not be sent: the
  // backend keeps its own when only an unchanged total arrives.
  const wallboxesChanged = (): boolean =>
    wallboxes.join(",") !== initialWallboxes(prospect).join(",");
  const sendBreakdown = (): boolean =>
    !!prospect.station_evse_counts?.length || wallboxesChanged();

  const validate = (): boolean => {
    const next: Record<string, string> = {};
    if (!wallboxes.length || !wallboxes.every(isWholeCount)) {
      next.station_evse_counts = "Every wallbox needs at least 1 charge point";
    }
    ["first_name", "last_name", "email", "iban", "bic", "account_holder_name"].forEach(
      (f) => {
        if (!String(form[f] ?? "").trim()) next[f] = "Required";
      }
    );
    if (form.iban && !IBAN_RE.test(String(form.iban).replace(/\s/g, "").toUpperCase())) {
      next.iban = "That doesn't look like a valid IBAN";
    }
    if (form.bic && !BIC_RE.test(String(form.bic).replace(/\s/g, "").toUpperCase())) {
      next.bic = "That doesn't look like a valid BIC";
    }
    if (isBusiness && prospect.vat_required && form.vat_number) {
      const res = validateEuVatId(String(form.vat_number));
      // strict:false doesn't narrow the discriminated union here — cast to the
      // error branch ([[project_tsconfig_strict_false]]).
      if (!res.ok) next.vat_number = (res as { ok: false; error: string }).error;
    }
    // A referral must name who referred them (mirrors the backend 422).
    if (
      form.lead_source === LEAD_SOURCE_NEEDING_DETAIL &&
      !String(form.referred_by ?? "").trim()
    ) {
      next.referred_by = "Tell us how they found us";
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  // The backend PUT reuses the full create request (all required fields), so we
  // send the complete current snapshot with ops edits overlaid — NOT a diff.
  const REQUEST_FIELDS: string[] = [
    "customer_type",
    "company_name",
    "vat_required",
    "vat_number",
    "first_name",
    "last_name",
    "email",
    "telephone",
    "billing_street",
    "billing_postcode",
    "billing_city",
    "billing_country",
    "station_same_as_billing",
    "station_street",
    "station_postcode",
    "station_city",
    "station_country",
    "latitude",
    "longitude",
    "charge_point_count",
    "wallbox_manufacturer",
    "wallbox_model",
    "account_holder_name",
    "iban",
    "bic",
    "bank_name",
    "discovery_source",
    "salutation",
    "sign_method",
    "lead_source",
    "referred_by",
  ];

  const buildPayload = (): ProspectUpdatePayload => {
    const payload: Record<string, any> = {};
    REQUEST_FIELDS.forEach((f) => {
      payload[f] = (prospect as any)[f];
    });
    EDITABLE_FIELDS.forEach((f) => {
      // Keep company/VAT untouched (null) for private prospects — the inputs
      // aren't rendered, so the form holds an empty string we must not send.
      if ((f === "company_name" || f === "vat_number") && !isBusiness) return;
      // Only ever send a location type that came from the lookup. The backend
      // silently discards a value it cannot parse and still answers 200, so
      // sending a label (or a stale string) would wipe the field.
      if (f === "location_type") {
        const value = form.location_type;
        if (
          value &&
          locationTypes.options.some((o) => o.value === value)
        ) {
          payload.location_type = value;
        }
        return;
      }
      payload[f as string] = form[f as string];
    });
    // A referral name only applies to REFERRAL — clear it otherwise so we don't
    // persist a stale name after the lead source changes.
    // Only cleared for an answer that has no follow-up. The old rule cleared it
    // for anything that was not REFERRAL, which erased an "Other" prospect's own
    // answer on any unrelated edit (CWA-154, FR-029).
    if (payload.lead_source !== LEAD_SOURCE_NEEDING_DETAIL) {
      payload.referred_by = null;
    }
    // Always the breakdown with its total: the total alone reads as that many
    // wallboxes with one charge point each, which reshaped the hardware on any
    // unrelated edit (CWA-201).
    if (sendBreakdown()) {
      const counts = wallboxCounts();
      payload.station_evse_counts = counts;
      payload.charge_point_count = counts.reduce((a, b) => a + b, 0);
    }
    return payload as ProspectUpdatePayload;
  };

  const hasChanges = (): boolean =>
    wallboxesChanged() ||
    EDITABLE_FIELDS.some((f) => {
      // Compare identifiers on both sides — the prospect holds a label here, so
      // a naive comparison would report a change on every open.
      if (f === "location_type") {
        return (
          form.location_type !==
          locationTypes.valueForLabel(prospect.location_type)
        );
      }
      return form[f as string] !== ((prospect as any)[f] ?? "");
    });

  const submit = async () => {
    if (!validate()) return;
    if (!hasChanges()) {
      onHide();
      return;
    }
    const payload = buildPayload();
    setSubmitting(true);
    try {
      const ok = await onSave(payload);
      if (ok) onHide();
    } catch (e: any) {
      // Map server-side 422 field errors to inline messages.
      const fields: string[] = e?.response?.data?.fields ?? [];
      if (fields.length) {
        const mapped: Record<string, string> = {};
        fields.forEach((f) => {
          mapped[f] = "Rejected by the server — please check this value";
        });
        setErrors(mapped);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const footer = (
    <div>
      <Button label="Cancel" text onClick={onHide} disabled={submitting} />
      {editable && (
        <Button
          label="Save changes"
          icon="pi pi-check"
          onClick={submit}
          loading={submitting}
        />
      )}
    </div>
  );

  const renderEnumDropdown = (
    name: string,
    label: string,
    options: { value: string; label: string }[],
    required = false
  ) => (
    <div key={name} className="field col-12 md:col-6">
      <InputLabel
        name={name}
        label={label}
        required={required}
        errorMessage={errors[name]}
      />
      <Dropdown
        inputId={name}
        value={form[name] ?? null}
        options={options}
        optionLabel="label"
        optionValue="value"
        onChange={(e) => setField(name, e.value)}
        placeholder="Select"
        className="w-full"
      />
    </div>
  );

  const renderSalutation = () => (
    <div key="salutation" className="field col-12 md:col-6">
      <InputLabel name="salutation" label="Salutation" errorMessage={errors.salutation} />
      <div className="flex gap-3 mt-1">
        {SALUTATION_OPTIONS.map((opt) => (
          <div key={opt.value} className="flex align-items-center gap-2">
            <RadioButton
              inputId={`salutation_${opt.value}`}
              name="salutation"
              value={opt.value}
              checked={form.salutation === opt.value}
              onChange={(e) => setField("salutation", e.value)}
            />
            <label htmlFor={`salutation_${opt.value}`}>{opt.label}</label>
          </div>
        ))}
      </div>
    </div>
  );

  // Where the station is used. Options come from the backend so they can never
  // drift from what the public form offers; if the lookup is unavailable the
  // field explains itself and is simply omitted from the payload.
  const renderLocationType = () => {
    const selected = locationTypes.options.find(
      (o) => o.value === form.location_type
    );
    return (
      <div key="location_type" className="field col-12">
        <InputLabel
          name="location_type"
          label="Where the station is used"
          errorMessage={errors.location_type}
        />
        <Dropdown
          inputId="location_type"
          value={form.location_type ?? null}
          options={locationTypes.options}
          optionLabel="label"
          optionValue="value"
          onChange={(e) => setField("location_type", e.value)}
          placeholder={
            locationTypes.error
              ? "Temporarily unavailable"
              : locationTypes.loading
                ? "Loading…"
                : "Select"
          }
          disabled={locationTypes.error || locationTypes.loading}
          className="w-full"
        />
        {locationTypes.error ? (
          <small className="text-500">
            The list of location types could not be loaded, so this field cannot
            be changed right now. Everything else on this form still saves.
          </small>
        ) : (
          selected && <small className="text-500">{selected.description}</small>
        )}
      </div>
    );
  };

  const renderTextField = (name: keyof ProspectUpdatePayload) => (
    <TextInputField
      key={name as string}
      name={name as string}
      label={fieldLabels[name as string] ?? (name as string)}
      value={form[name as string] ?? ""}
      onChange={(e: any) => setField(name as string, e.target.value)}
      errorMessage={errors[name as string]}
      required={["first_name", "last_name", "email", "iban", "bic", "account_holder_name"].includes(
        name as string
      )}
    />
  );

  const renderWallboxes = () => {
    const counts = wallboxCounts();
    const total = counts.every((n) => !isNaN(n)) ? counts.reduce((a, b) => a + b, 0) : null;
    const assigned = prospect.technical_names?.length ?? 0;
    return (
      <div key="wallboxes" className="field col-12">
        <InputLabel
          name="station_evse_counts"
          label="Wallboxes and charge points"
          errorMessage={errors.station_evse_counts}
        />
        {wallboxes.map((value, index) => (
          <div key={index} className="flex align-items-center gap-2 mb-2">
            <label htmlFor={`wallbox_${index}`} className="w-8rem">
              Wallbox {index + 1}
            </label>
            <InputText
              id={`wallbox_${index}`}
              aria-label={`Charge points on wallbox ${index + 1}`}
              value={value}
              keyfilter="pint"
              onChange={(e) => setChargePoints(index, e.target.value)}
              style={{ width: "6rem" }}
            />
            <span className="text-500">charge points</span>
            <Button
              type="button"
              icon="pi pi-trash"
              text
              severity="danger"
              aria-label={`Remove wallbox ${index + 1}`}
              disabled={wallboxes.length <= 1}
              onClick={() => removeWallbox(index)}
            />
          </div>
        ))}
        <div className="flex align-items-center gap-3">
          <Button
            type="button"
            label="Add wallbox"
            icon="pi pi-plus"
            text
            onClick={addWallbox}
            style={{ width: "auto" }}
          />
          {total !== null && (
            <small className="text-500">
              {plural(total, "charge point", "charge points")} on{" "}
              {plural(wallboxes.length, "wallbox", "wallboxes")}
            </small>
          )}
        </div>
        {assigned > 0 && assigned !== wallboxes.length && (
          <Message
            severity="warn"
            className="w-full mt-2"
            text={`${plural(assigned, "station ID is", "station IDs are")} assigned for ${plural(
              wallboxes.length,
              "wallbox",
              "wallboxes"
            )}. Re-assign the station IDs after saving so each wallbox has one.`}
          />
        )}
      </div>
    );
  };

  const renderField = (name: keyof ProspectUpdatePayload) => {
    if (name === "company_name" || name === "vat_number") {
      if (!isBusiness) return null;
    }
    if (name === "salutation") return renderSalutation();
    if (name === "location_type") return renderLocationType();
    if (name === "wallbox_model") {
      // The hardware breakdown sits with the wallbox it describes.
      return [renderTextField(name), renderWallboxes()];
    }
    if (name === "sign_method")
      return renderEnumDropdown("sign_method", "Signature method", SIGN_METHOD_OPTIONS);
    if (name === "lead_source")
      return renderEnumDropdown("lead_source", "Lead source", LEAD_SOURCE_OPTIONS);
    if (name === "referred_by") {
      // The follow-up belongs to "Other" — required in that case. A prospect
      // still holding the retired "Referral" answer keeps its text visible so an
      // unrelated edit cannot silently drop it.
      const isOther = form.lead_source === LEAD_SOURCE_NEEDING_DETAIL;
      const isLegacyReferral = form.lead_source === "REFERRAL";
      if (!isOther && !isLegacyReferral) return null;
      return (
        <TextInputField
          key="referred_by"
          name="referred_by"
          label={isOther ? "Please specify" : "Referred by"}
          value={form.referred_by ?? ""}
          onChange={(e: any) => setField("referred_by", e.target.value)}
          errorMessage={errors.referred_by}
          required={isOther}
        />
      );
    }
    return renderTextField(name);
  };

  return (
    <Dialog
      header="Edit prospect"
      visible={visible}
      onHide={onHide}
      footer={footer}
      style={{ width: "55rem" }}
      maximizable
    >
      {!editable && (
        <Message
          severity="warn"
          className="w-full mb-3"
          text="This prospect is locked and can no longer be edited."
        />
      )}
      <fieldset disabled={!editable} className="border-none p-0 m-0">
        <div className="grid p-fluid formgrid">
          {EDITABLE_FIELDS.map(renderField)}
        </div>
      </fieldset>
    </Dialog>
  );
};

export default EditProspectDialog;
